/**
 * Admin fatture API client — F-011 (rescoped 2026-07-08).
 *
 *  - GET   /api/v1/admin/fatture           → { items, total, page, limit }
 *  - PATCH /api/v1/admin/fatture/:id       → FatturaView (marca fatturata)
 *
 * Convenzioni come `admin-servizi/lib/servizi-client.ts`. Importo BigInt →
 * stringa (boundary HTTP); conversione a euro fatta nel componente.
 */
const API = '/api/v1/admin/fatture';

export interface FatturaBillingSnapshot {
  type: string | null;
  firstName: string | null;
  lastName: string | null;
  codiceFiscale: string | null;
  ragioneSociale: string | null;
  piva: string | null;
  sdiCode: string | null;
  sdiPec: string | null;
  indirizzo: string | null;
  cap: string | null;
  citta: string | null;
  provincia: string | null;
  nazione: string | null;
  email: string;
}

export type FatturaTipo = 'RICARICA_WALLET' | 'PAGAMENTO_PRATICA';
export type FatturaStato = 'DA_FATTURARE' | 'FATTURATA';

export interface FatturaView {
  id: string;
  tipo: FatturaTipo;
  accountId: string;
  importoCents: string;
  valuta: string;
  paymentRef: string;
  praticaId: string | null;
  billingSnapshot: FatturaBillingSnapshot;
  stato: FatturaStato;
  numeroFattura: string | null;
  fatturataAt: string | null;
  fatturataBy: string | null;
  createdAt: string;
  account: { email: string; name: string | null };
}

export interface FattureListResponse {
  items: FatturaView[];
  total: number;
  page: number;
  limit: number;
}

export interface ListFattureFilters {
  stato?: FatturaStato;
  page?: number;
  limit?: number;
}

export class FattureApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'FattureApiError';
    this.status = status;
    this.code = code;
  }
}

const ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Dati non validi. Controlla i campi.',
  FATTURA_NOT_FOUND: 'Voce non trovata.',
  FATTURA_ALREADY_FATTURATA: 'Questa voce è già stata marcata come fatturata.',
  FORBIDDEN: 'Accesso negato.',
  UNAUTHORIZED: "Sessione scaduta. Effettua di nuovo l'accesso.",
};

export function errorMessageFor(err: unknown): string {
  if (err instanceof FattureApiError) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  if (err instanceof TypeError) return 'Errore di connessione. Riprova.';
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function throwForError(res: Response, fallbackCode: string, fallbackMsg: string): Promise<never> {
  const body = (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
  throw new FattureApiError(res.status, body.code ?? fallbackCode, body.message ?? fallbackMsg);
}

export async function listFatture(filters: ListFattureFilters = {}): Promise<FattureListResponse> {
  const params = new URLSearchParams();
  if (filters.stato) params.set('stato', filters.stato);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();

  const res = await fetch(`${API}${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'LIST_FAILED', 'Impossibile caricare le fatture da emettere');
  return (await res.json()) as FattureListResponse;
}

export interface MarkFatturataBody {
  numeroFattura?: string;
}

export async function markFatturata(id: string, body: MarkFatturataBody): Promise<FatturaView> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwForError(res, 'MARK_FAILED', 'Impossibile marcare la voce come fatturata');
  return (await res.json()) as FatturaView;
}
